import type {ICreditQuote, ICreditRates} from "../models/interfaces/credits.interface.ts";

// C-1 rate table; used only until the catalog (which serves the same table) has loaded.
export const DEFAULT_CREDIT_RATES: ICreditRates = {
  creditUsd: 0.5,
  transcriptionPerMinute: 2,
  firstTranslationPerMinute: 4,
  additionalTranslationPerMinute: 3,
  dubbingScriptPerMinutePerLanguage: 4,
  epgPerProduct: 50,
};

export const billedMinutesForFile = (seconds: number): number =>
  Number.isFinite(seconds) && seconds > 0
    ? Math.max(1, Math.floor(seconds / 60 + 0.5)) // nearest minute, halves up, at least one
    : 0;

export const quoteCreateCredits = (
  fileDurationsSeconds: number[],
  targetCount: number,
  dubbingScript: boolean,
  rates: ICreditRates,
): ICreditQuote => {
  const quote = fileDurationsSeconds.reduce<ICreditQuote>(
    (current, seconds) => {
      const billedMinutes = billedMinutesForFile(seconds);
      const transcriptionCredits =
        targetCount === 0 ? rates.transcriptionPerMinute * billedMinutes : 0;
      const translationCredits =
        targetCount === 0
          ? 0
          : (rates.firstTranslationPerMinute +
              rates.additionalTranslationPerMinute * (targetCount - 1)) *
            billedMinutes;
      const dubbingCredits =
        dubbingScript
          ? (targetCount === 0
              ? rates.dubbingScriptPerMinutePerLanguage
              : rates.dubbingScriptPerMinutePerLanguage +
                rates.additionalTranslationPerMinute * (targetCount - 1)) *
            billedMinutes
          : 0;
      return {
        billedMinutes: current.billedMinutes + billedMinutes,
        transcriptionCredits: current.transcriptionCredits + transcriptionCredits,
        translationCredits: current.translationCredits + translationCredits,
        dubbingCredits: current.dubbingCredits + dubbingCredits,
        total: current.total + transcriptionCredits + translationCredits + dubbingCredits,
      };
    },
    {billedMinutes: 0, transcriptionCredits: 0, translationCredits: 0, dubbingCredits: 0, total: 0},
  );
  return quote;
};

export const quoteAddLanguagesCredits = (
  seconds: number,
  newCount: number,
  existingTranslationCount: number,
  dubbingScript: boolean,
  rates: ICreditRates,
): ICreditQuote => {
  const billedMinutes = billedMinutesForFile(seconds);
  const translationCredits =
    (existingTranslationCount === 0
      ? rates.transcriptionPerMinute + rates.additionalTranslationPerMinute * Math.max(newCount - 1, 0)
      : rates.additionalTranslationPerMinute * newCount) * billedMinutes;
  const dubbingCredits =
    rates.additionalTranslationPerMinute * billedMinutes * newCount * (dubbingScript ? 1 : 0);
  return {
    billedMinutes,
    transcriptionCredits: 0,
    translationCredits,
    dubbingCredits,
    total: translationCredits + dubbingCredits,
  };
};
